import React, { useEffect, useMemo, useState } from "react";
import { db } from "../../firebase";
import {
  collection,
  query,
  orderBy,
  limit,
  onSnapshot,
} from "firebase/firestore";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";
import { DollarSign, TrendingUp } from "lucide-react";

const clp = (n) =>
  new Intl.NumberFormat("es-CL", {
    style: "currency",
    currency: "CLP",
    maximumFractionDigits: 0,
  }).format(n || 0);

const Skeleton = ({ className }) => (
  <div className={`animate-pulse bg-gray-200/70 rounded-md ${className}`} />
);

// ================== HELPERS LABELS CHARTS ==================
const cleanLabel = (s = "") =>
  String(s).replace(/\s+/g, " ").replace(/\s-\s/g, " · ").trim();

const wrapLabel = (text, maxCharsPerLine = 24, maxLines = 3) => {
  const t = cleanLabel(text);
  if (!t) return ["—"];

  const words = t.split(" ");
  const lines = [];
  let line = "";

  for (const w of words) {
    const next = line ? `${line} ${w}` : w;
    if (next.length <= maxCharsPerLine) {
      line = next;
    } else {
      if (line) lines.push(line);
      line = w;
      if (lines.length >= maxLines - 1) break;
    }
  }

  if (line && lines.length < maxLines) lines.push(line);

  const used = lines.join(" ").length;
  if (used < t.length) {
    lines[lines.length - 1] = lines[lines.length - 1].replace(/\.*$/, "") + "…";
  }

  return lines;
};

const splitServicioLabel = (s = "") => {
  const t = cleanLabel(s);
  const parts = t.split(" · ");
  const categoria = parts[0] || "—";
  const variante = parts.slice(1).join(" · ").trim();
  return { categoria, variante };
};

const YAxisTickMultiline = ({ x, y, payload }) => {
  const { categoria, variante } = splitServicioLabel(payload?.value);

  const catLines = wrapLabel(categoria, 26, 2);
  const varLines = variante ? wrapLabel(variante, 26, 2) : [];
  const GAP = 14;
  const lines = [...catLines, ...varLines];

  return (
    <g transform={`translate(${x},${y})`}>
      <text x={-GAP} y={0} dy={4} textAnchor="end" fontSize={12} fill="#334155">
        {lines.map((ln, i) => (
          <tspan key={i} x={-GAP} dy={i === 0 ? 0 : 14}>
            {ln}
          </tspan>
        ))}
      </text>
    </g>
  );
};

const getYAxisWidthTop = (items = []) => {
  const pxPerChar = 6.6;
  const padding = 22;

  let maxChars = 0;

  items.forEach((it) => {
    const { categoria, variante } = splitServicioLabel(it?.nombre || "");
    const lines = [
      ...wrapLabel(categoria, 26, 2),
      ...(variante ? wrapLabel(variante, 26, 2) : []),
    ];
    lines.forEach((l) => {
      maxChars = Math.max(maxChars, (l || "").length);
    });
  });

  const est = Math.round(maxChars * pxPerChar + padding);
  return Math.max(140, Math.min(220, est));
};

// ================== INGRESO DEVENGADO POR SESIÓN (REAL) ==================
// Tu regla: el ingreso se reconoce SOLO si estado == "realizada".
// estadoSesion solo describe si asistió o no (completada vs realizada), no afecta ingreso.

const esReservaRealizada = (d) => (d?.estado || "").toLowerCase() === "realizada";

// fecha contable = día trabajado. Preferimos d.fecha ("YYYY-MM-DD").
const getFechaContable = (d) => {
  if (esReservaRealizada(d)) {
    if (typeof d.fecha === "string" && /^\d{4}-\d{2}-\d{2}$/.test(d.fecha)) {
      return new Date(d.fecha + "T00:00:00");
    }
  }
  // fallback si faltara fecha
  return d?.creadoEn?.toDate ? d.creadoEn.toDate() : null;
};

// si en algún caso tuvieras el docId de planes_usuarios guardado en la reserva:
const getPlanUsuarioId = (r) =>
  r?.planUsuarioId ||
  r?.plan_usuario_id ||
  r?.planUsuario_id ||
  r?.planUsuariosId ||
  r?.plan_usuarios_id ||
  r?.planId ||
  null;

// valor sesión desde planes_usuarios (si aplica)
const hasValue = (v) => v !== undefined && v !== null && v !== "";

const esGratisPorDescuentoTotal = (obj = {}) => {
  const num = (...vals) => {
    for (const v of vals) {
      if (v !== undefined && v !== null && v !== "") {
        const n = Number(v);
        if (!Number.isNaN(n)) return n;
      }
    }
    return null;
  };

  const str = (...vals) => {
    for (const v of vals) {
      if (v !== undefined && v !== null && v !== "") {
        return String(v).toLowerCase();
      }
    }
    return "";
  };

  const montoOriginal = num(obj?.monto_original, obj?.precio_original);
  const cuponDescuento = num(
    obj?.cupon_descuento,
    obj?.descuento_monto,
    obj?.monto_descuento
  );
  const descuentoPct = num(
    obj?.descuento_porcentaje,
    obj?.porcentaje_descuento,
    obj?.cupon_porcentaje
  );
  const precioTotal = num(obj?.precio_total, obj?.precio_final, obj?.monto_final);
  const precioPorSesion = num(obj?.precio_por_sesion, obj?.valor_por_sesion);
  const cuponCodigo = str(obj?.cupon_codigo);

  // Gratis explícito
  if (precioPorSesion === 0 && precioTotal === 0) return true;

  // Cupón 100% por monto
  if (
    montoOriginal !== null &&
    cuponDescuento !== null &&
    montoOriginal > 0 &&
    cuponDescuento >= montoOriginal
  ) {
    return true;
  }

  // Cupón 100% por porcentaje
  if (descuentoPct !== null && descuentoPct >= 100) return true;

  // Si tiene cupón y además total final = 0
  if (cuponCodigo && precioTotal === 0) return true;

  // ✅ NUEVO: si monto_pagado es 0 y tiene cupón aplicado
  if (cuponCodigo && num(obj?.monto_pagado) === 0) return true;

  // ✅ NUEVO: si precio_por_sesion === 0 explícitamente en la reserva/plan
  if (precioPorSesion === 0) return true;

  return false;
};

const getPrecioSesionFromPlanUsuario = (plan) => {
  if (!plan) return null;

  // Si el plan fue gratis por cupón/descuento total
  if (esGratisPorDescuentoTotal(plan)) return 0;

  // Si trae precio por sesión calculado
  if (hasValue(plan?.precio_por_sesion)) {
    return Math.max(0, Number(plan.precio_por_sesion));
  }

  // Si trae total real pagado y sesiones, prorratear
  if (
    Number(plan?.sesiones_totales) > 0 &&
    hasValue(plan?.pago_total_realizado)
  ) {
    return Math.max(
      0,
      Number(plan.pago_total_realizado) / Number(plan.sesiones_totales)
    );
  }

  return null;
};

const getIngresoReconocido = (reserva, planUsuariosMap) => {
  if (!esReservaRealizada(reserva)) return 0;

  // 1) Si la reserva misma evidencia gratuidad total, devolver 0
  if (esGratisPorDescuentoTotal(reserva)) {
    return 0;
  }

  // 2) Si está ligada a un plan, usar el valor del plan
  const planUsuarioId = getPlanUsuarioId(reserva);
  if (planUsuarioId && planUsuariosMap?.has(planUsuarioId)) {
    const planU = planUsuariosMap.get(planUsuarioId);

    if (esGratisPorDescuentoTotal(planU)) return 0;

    const v = getPrecioSesionFromPlanUsuario(planU);
    if (v !== null) return Math.max(0, v);
  }

  // 3) Si la reserva trae precio por sesión, usarlo
  if (hasValue(reserva?.precio_por_sesion)) {
    return Math.max(0, Number(reserva.precio_por_sesion));
  }

  // 4) Fallback a valores netos/finales
  if (hasValue(reserva?.precio_final)) {
    return Math.max(0, Number(reserva.precio_final));
  }
  if (hasValue(reserva?.monto_pagado)) {
    return Math.max(0, Number(reserva.monto_pagado));
  }
  if (hasValue(reserva?.pago_total_realizado)) {
    return Math.max(0, Number(reserva.pago_total_realizado));
  }
  if (hasValue(reserva?.precio_total)) {
    return Math.max(0, Number(reserva.precio_total));
  }

  return 0;
};

const cuentaComoTransferencia = (d) => {
  const metodo = (d?.pago_metodo ?? "").toString().trim().toLowerCase();
  return metodo === "transferencia" || metodo === "" || metodo === "null";
};

const getTransferenciasMesRows = (docs) => {
  const now = new Date();
  const iniMes = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0);
  const nextMes = new Date(now.getFullYear(), now.getMonth() + 1, 1, 0, 0, 0, 0);

  return (docs || [])
    .filter((d) => {
      const creado = d?.creadoEn?.toDate ? d.creadoEn.toDate() : null;
      if (!creado) return false;
      if (creado < iniMes || creado >= nextMes) return false;

      const estado = String(d?.estado || "").toLowerCase();
      if (!["pendiente", "aprobada", "realizada"].includes(estado)) return false;

      return true;
    })
    .map((d) => ({
      ...d,
      _when: d?.creadoEn?.toDate ? d.creadoEn.toDate() : null,
      _monto: Number(d?.monto_pagado || 0),
      _cuentaTransferencia: cuentaComoTransferencia(d),
    }))
    .filter((d) => d._cuentaTransferencia && d._monto > 0)
    .sort((a, b) => (b._when?.getTime() || 0) - (a._when?.getTime() || 0));
};

const ESTADOS_OCUPACION_VALIDOS = new Set(["pendiente", "aprobada", "realizada"]);

const esReservaContableEnOcupacion = (d) =>
  ESTADOS_OCUPACION_VALIDOS.has(String(d?.estado || "").toLowerCase());

const esEntrenamientoPareja = (d) => {
  const categoria = String(d?.servicio_categoria || "").toLowerCase();
  const variante = String(d?.servicio_variante || "").toLowerCase();
  const raw = String(d?.servicio_raw || "").toLowerCase();
  const texto = `${categoria} ${variante} ${raw}`;

  return texto.includes("entrenamiento en pareja");
};

const getPacientesAtendidosReserva = (d) => {
  if (!esReservaContableEnOcupacion(d)) return 0;
  return esEntrenamientoPareja(d) ? 2 : 1;
};

const buildDailyOccupancySeries = (docs, daysBack = 7, selectedDate = null) => {
  const map = new Map();

  if (selectedDate) {
    const key = selectedDate;
    map.set(key, 0);

    (docs || []).forEach((d) => {
      if (d?.fecha !== key) return;
      map.set(key, (map.get(key) || 0) + getPacientesAtendidosReserva(d));
    });

    return Array.from(map.entries()).map(([dia, pacientes]) => ({
      dia,
      pacientes,
    }));
  }

  const now = new Date();
  for (let i = (daysBack || 7) - 1; i >= 0; i--) {
    const d = new Date(now);
    d.setDate(now.getDate() - i);
    const key = d.toISOString().slice(0, 10);
    map.set(key, 0);
  }

  (docs || []).forEach((d) => {
    const f = d?.fecha;
    if (!map.has(f)) return;
    map.set(f, (map.get(f) || 0) + getPacientesAtendidosReserva(d));
  });

  return Array.from(map.entries()).map(([dia, pacientes]) => ({
    dia,
    pacientes,
  }));
};

const formatMonthKey = (date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;

const formatMonthLabel = (monthKey) => {
  if (!monthKey) return "—";
  const [y, m] = monthKey.split("-").map(Number);
  const dt = new Date(y, (m || 1) - 1, 1);
  return new Intl.DateTimeFormat("es-CL", {
    month: "short",
    year: "numeric",
  }).format(dt);
};

const ESTADO_COLOR_MAP = {
  pendiente: "#f59e0b", // amarillo/naranjo
  aprobada: "#10b981",  // verde
  rechazada: "#ef4444", // rojo
  cancelada: "#6366f1", // violeta
  realizada: "#0ea5e9", // celeste
  "—": "#94a3b8",       // gris fallback
};

const getEstadoColor = (estado) => {
  const key = String(estado || "—").toLowerCase().trim();
  return ESTADO_COLOR_MAP[key] || "#94a3b8";
};

const formatDateKey = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const getDateFromReserva = (d) => {
  if (d?.creadoEn?.toDate) return d.creadoEn.toDate();
  if (typeof d?.fecha === "string" && /^\d{4}-\d{2}-\d{2}$/.test(d.fecha)) {
    return new Date(d.fecha + "T00:00:00");
  }
  return null;
};

const getRangeStartDate = (range) => {
  if (range === "all") return null;

  const now = new Date();
  const start = new Date(now);

  switch (range) {
    case 7:
      start.setDate(now.getDate() - 7);
      return start;
    case 30:
      start.setDate(now.getDate() - 30);
      return start;
    case 90:
      start.setDate(now.getDate() - 90);
      return start;
    case 180:
      start.setDate(now.getDate() - 180);
      return start;
    case 365:
      start.setDate(now.getDate() - 365);
      return start;
    default:
      start.setDate(now.getDate() - 30);
      return start;
  }
};

const getRangeLabel = (range) => {
  switch (range) {
    case 7:
      return "últimos 7 días";
    case 30:
      return "últimos 30 días";
    case 90:
      return "últimos 3 meses";
    case 180:
      return "últimos 6 meses";
    case 365:
      return "últimos 12 meses";
    case "all":
      return "todo el historial";
    default:
      return "últimos 30 días";
  }
};

const buildMixEstadosSeries = (docs, range = 30) => {
  const start = getRangeStartDate(range);
  const contEstados = new Map();

  (docs || []).forEach((d) => {
    const when = getDateFromReserva(d);
    if (!when) return;
    if (start && when < start) return;

    const est = (d.estado || "—").toString();
    contEstados.set(est, (contEstados.get(est) || 0) + 1);
  });

  return Array.from(contEstados.entries())
    .map(([estado, cantidad]) => ({ estado, cantidad }))
    .sort((a, b) => b.cantidad - a.cantidad);
};

const buildMonthlyIncomeSeries = (docs, planUsuariosMap, monthsBack = 6) => {
  const realizadas = (docs || []).filter((d) => esReservaRealizada(d));

  const monthlyMap = new Map();

  realizadas.forEach((d) => {
    const dt = getFechaContable(d);
    if (!dt) return;

    const key = formatMonthKey(dt);
    monthlyMap.set(
      key,
      (monthlyMap.get(key) || 0) + getIngresoReconocido(d, planUsuariosMap)
    );
  });

  const sortedKeys = Array.from(monthlyMap.keys()).sort();

  if (sortedKeys.length === 0) {
    const now = new Date();
    const fallback = [];
    for (let i = (monthsBack || 6) - 1; i >= 0; i--) {
      const dt = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const key = formatMonthKey(dt);
      fallback.push({
        mes: key,
        mesLabel: key,
        total: 0,
      });
    }
    return fallback;
  }

  let keysToShow = sortedKeys;

  if (monthsBack !== "all") {
    const n = Number(monthsBack) || 6;
    const lastKey = sortedKeys[sortedKeys.length - 1];
    const [yy, mm] = lastKey.split("-").map(Number);
    const end = new Date(yy, mm - 1, 1);

    const rangeMap = new Map();
    for (let i = n - 1; i >= 0; i--) {
      const dt = new Date(end.getFullYear(), end.getMonth() - i, 1);
      const key = formatMonthKey(dt);
      rangeMap.set(key, monthlyMap.get(key) || 0);
    }
    keysToShow = Array.from(rangeMap.keys());

    return keysToShow.map((key) => ({
      mes: key,
      mesLabel: key,
      total: rangeMap.get(key) || 0,
    }));
  }

  return keysToShow.map((key) => ({
    mes: key,
    mesLabel: key,
    total: monthlyMap.get(key) || 0,
  }));
};

// ======================================================
const Badge = ({ children }) => (
  <span className="inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-700">
    {children}
  </span>
);

const KpiDetailModal = ({
  open,
  onClose,
  kpiKey,
  reservas,
  planUsuariosMap,
  detailMeta,
}) => {
  const data = useMemo(() => {
    const now = new Date();
    const startMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const desde30 = new Date(now);
    desde30.setDate(now.getDate() - 30);

    const base = reservas || [];

    if (kpiKey === "ingresosMes") {
      const rows = base
        .filter((d) => esReservaRealizada(d))
        .filter((d) => {
          const f = getFechaContable(d);
          return f && f >= startMonth;
        })
        .map((d) => ({
          ...d,
          _when: getFechaContable(d),
          _ingreso: getIngresoReconocido(d, planUsuariosMap),
        }))
        .sort((a, b) => (b._when?.getTime() || 0) - (a._when?.getTime() || 0));

      const total = rows.reduce((a, r) => a + (r._ingreso || 0), 0);

      const porServicio = new Map();
      rows.forEach((r) => {
        const cat = (r.servicio_categoria || "Servicio").toString();
        const varr = (r.servicio_variante || "").toString();
        const key = varr ? `${cat} · ${varr}` : cat;
        porServicio.set(key, (porServicio.get(key) || 0) + (r._ingreso || 0));
      });

      const top = Array.from(porServicio.entries())
        .map(([nombre, total]) => ({ nombre, total }))
        .sort((a, b) => b.total - a.total)
        .slice(0, 10);

      return { title: "Ingresos del mes (realizadas)", rows, total, top };
    }

    if (kpiKey === "ticketPromedioMes") {
      const rows = base
        .filter((d) => esReservaRealizada(d))
        .filter((d) => {
          const f = getFechaContable(d);
          return f && f >= startMonth;
        })
        .map((d) => ({
          ...d,
          _when: getFechaContable(d),
          _ingreso: getIngresoReconocido(d, planUsuariosMap),
        }))
        .sort((a, b) => (b._when?.getTime() || 0) - (a._when?.getTime() || 0));

      const total = rows.reduce((a, r) => a + (r._ingreso || 0), 0);
      const ticket = rows.length ? Math.round(total / rows.length) : 0;

      return { title: "Ticket promedio del mes (realizadas)", rows, total, ticket };
    }

    if (kpiKey === "ocupacionSemana") {
      const end = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const start = new Date(end);
      start.setDate(end.getDate() - 6);

      const seven = new Map();
      for (let i = 0; i < 7; i++) {
        const d = new Date(start);
        d.setDate(start.getDate() + i);
        const key = d.toISOString().slice(0, 10);
        seven.set(key, 0);
      }

      base.forEach((d) => {
        const f = d.fecha;
        if (!seven.has(f)) return;
        seven.set(f, (seven.get(f) || 0) + getPacientesAtendidosReserva(d));
      });

      const serie = Array.from(seven.entries()).map(([dia, pacientes]) => ({
        dia,
        pacientes,
      }));

      const rows = base
        .filter((d) => seven.has(d.fecha))
        .filter((d) => esReservaContableEnOcupacion(d))
        .map((d) => ({
          ...d,
          _dia: d.fecha,
          _pacientes: getPacientesAtendidosReserva(d),
        }))
        .sort((a, b) => (a._dia || "").localeCompare(b._dia || ""));

      return { title: "Ocupación semanal", rows, total: rows.length, serie };
    }

    if (kpiKey === "conversion30d") {
      const rows = base
        .filter((d) => d?.creadoEn?.toDate && d.creadoEn.toDate() >= desde30)
        .map((d) => ({
          ...d,
          _when: d.creadoEn?.toDate ? d.creadoEn.toDate() : null,
          _estado: d.estado || "—",
        }))
        .sort((a, b) => (b._when?.getTime() || 0) - (a._when?.getTime() || 0));

      const tot = rows.length;
      const conv = rows.filter((r) => ["aprobada", "realizada"].includes((r._estado || "").toLowerCase())).length;
      const conversion = tot ? Math.round((conv / tot) * 100) : 0;

      const estados = new Map();
      rows.forEach((r) => estados.set(r._estado, (estados.get(r._estado) || 0) + 1));
      const mix = Array.from(estados.entries())
        .map(([estado, cantidad]) => ({ estado, cantidad }))
        .sort((a, b) => b.cantidad - a.cantidad);

      return { title: "Conversión (30 días)", rows, tot, conv, conversion, mix };
    }

      if (kpiKey === "ingresosHistorico") {
      const selectedMonth = detailMeta?.monthKey || formatMonthKey(new Date());

      const rows = base
        .filter((d) => esReservaRealizada(d))
        .map((d) => ({
          ...d,
          _when: getFechaContable(d),
          _monthKey: getFechaContable(d) ? formatMonthKey(getFechaContable(d)) : null,
          _ingreso: getIngresoReconocido(d, planUsuariosMap),
        }))
        .filter((d) => d._monthKey === selectedMonth)
        .sort((a, b) => (b._when?.getTime() || 0) - (a._when?.getTime() || 0));

      const total = rows.reduce((acc, r) => acc + (r._ingreso || 0), 0);

      const porServicio = new Map();
      rows.forEach((r) => {
        const cat = (r.servicio_categoria || "Servicio").toString();
        const varr = (r.servicio_variante || "").toString();
        const key = varr ? `${cat} · ${varr}` : cat;
        porServicio.set(key, (porServicio.get(key) || 0) + (r._ingreso || 0));
      });

      const top = Array.from(porServicio.entries())
        .map(([nombre, total]) => ({ nombre, total }))
        .sort((a, b) => b.total - a.total)
        .slice(0, 10);

      return {
        title: `Ingresos de ${formatMonthLabel(selectedMonth)}`,
        rows,
        total,
        top,
        monthKey: selectedMonth,
      };
    }

    if (kpiKey === "ocupacionDia") {
    const selectedDay = detailMeta?.date || new Date().toISOString().slice(0, 10);

    const rows = base
      .filter((d) => d?.fecha === selectedDay)
      .filter((d) => esReservaContableEnOcupacion(d))
      .map((d) => ({
        ...d,
        _dia: d.fecha,
        _pacientes: getPacientesAtendidosReserva(d),
      }))
      .sort((a, b) => {
        const ah = String(a?.hora || "");
        const bh = String(b?.hora || "");
        return ah.localeCompare(bh);
      });

    const totalPacientes = rows.reduce((acc, r) => acc + (r._pacientes || 0), 0);

    const porServicio = new Map();
    rows.forEach((r) => {
      const cat = (r.servicio_categoria || "Servicio").toString();
      const varr = (r.servicio_variante || "").toString();
      const key = varr ? `${cat} · ${varr}` : cat;
      porServicio.set(key, (porServicio.get(key) || 0) + (r._pacientes || 0));
    });

    const top = Array.from(porServicio.entries())
      .map(([nombre, total]) => ({ nombre, total }))
      .sort((a, b) => b.total - a.total)
      .slice(0, 10);

    const titleDate = new Intl.DateTimeFormat("es-CL", {
      weekday: "long",
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    }).format(new Date(selectedDay + "T00:00:00"));

    return {
      title: `Pacientes atendidos · ${titleDate}`,
      rows,
      total: totalPacientes,
      reservasCount: rows.length,
      top,
      selectedDay,
    };
  }

  if (kpiKey === "topServicio") {
  const servicioObjetivo = detailMeta?.servicioNombre || "";

  const rows = base
    .filter((d) => esReservaRealizada(d))
    .map((d) => {
      const categoria = cleanLabel(d.servicio_categoria || "Servicio");
      const variante = cleanLabel(d.servicio_variante || "");
      const nombreServicio = variante ? `${categoria} · ${variante}` : categoria;

      return {
        ...d,
        _when: getFechaContable(d),
        _ingreso: getIngresoReconocido(d, planUsuariosMap),
        _servicioNombre: nombreServicio,
      };
    })
    .filter((d) => d._servicioNombre === servicioObjetivo)
    .sort((a, b) => (b._when?.getTime() || 0) - (a._when?.getTime() || 0));

  const total = rows.reduce((acc, r) => acc + (r._ingreso || 0), 0);

  const pacientesMap = new Map();
  rows.forEach((r) => {
    const paciente =
      r.paciente_nombre || r.nombrePaciente || r.nombre || r.email || "—";

    if (!pacientesMap.has(paciente)) {
      pacientesMap.set(paciente, {
        paciente,
        sesiones: 0,
        total: 0,
      });
    }

    const item = pacientesMap.get(paciente);
    item.sesiones += 1;
    item.total += Number(r._ingreso || 0);
  });

  const topPacientes = Array.from(pacientesMap.values()).sort(
    (a, b) => b.total - a.total
  );

  return {
    title: `Detalle servicio · ${servicioObjetivo}`,
    rows,
    total,
    sesiones: rows.length,
    servicioNombre: servicioObjetivo,
    topPacientes,
  };
}

    if (kpiKey === "estadoReservaDetalle") {
      const estadoObjetivo = (detailMeta?.estado || "").toLowerCase();
      const range = detailMeta?.range ?? 30;
      const start = getRangeStartDate(range);

      const rows = base
        .map((d) => ({
          ...d,
          _when: getDateFromReserva(d),
          _estado: (d.estado || "—").toString(),
        }))
        .filter((d) => d._when)
        .filter((d) => {
          if (!estadoObjetivo) return true;
          return (d._estado || "").toLowerCase() === estadoObjetivo;
        })
        .filter((d) => {
          if (!start) return true;
          return d._when >= start;
        })
        .sort((a, b) => (b._when?.getTime() || 0) - (a._when?.getTime() || 0));

      return {
        title: `Reservas en estado "${detailMeta?.estado || "—"}" · ${getRangeLabel(range)}`,
        rows,
        total: rows.length,
        estado: detailMeta?.estado || "—",
        range,
      };
    }

      if (kpiKey === "transferenciasMes") {
      const rows = getTransferenciasMesRows(base);
      const total = rows.reduce((acc, r) => acc + Number(r._monto || 0), 0);

      return {
        title: "Transferencias del mes",
        rows,
        total,
      };
    }

    return { title: "Detalle", rows: [] };
}, [kpiKey, reservas, planUsuariosMap, detailMeta?.monthKey, detailMeta?.date, detailMeta?.servicioNombre, detailMeta?.estado, detailMeta?.range]);

  const maxSesiones = useMemo(() => {
    if (kpiKey !== "ocupacionSemana") return 1;
    const arr = data?.serie || [];
    const m = arr.reduce((acc, x) => Math.max(acc, Number(x.pacientes || 0)), 0);
    return m || 1;
  }, [kpiKey, data]);

  const [showTopServiciosMobile, setShowTopServiciosMobile] = useState(false);
  const isIncomeDetail =
    kpiKey === "ingresosMes" || kpiKey === "ingresosHistorico";

    useEffect(() => {
  setShowTopServiciosMobile(false);
}, [open, kpiKey, detailMeta?.monthKey]);

useEffect(() => {
  if (!open) return;

  const previousOverflow = document.body.style.overflow;
  const previousTouchAction = document.body.style.touchAction;

  document.body.style.overflow = "hidden";
  document.body.style.touchAction = "none";

  return () => {
    document.body.style.overflow = previousOverflow;
    document.body.style.touchAction = previousTouchAction;
  };
}, [open]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />

      <div className="absolute right-0 top-0 h-full w-full lg:w-[78vw] xl:w-[72vw] max-w-5xl bg-white shadow-2xl flex flex-col overflow-hidden">
        <div className="p-4 border-b flex items-start justify-between gap-3">
          <div>
            <div className="text-sm text-slate-500">Detalle KPI</div>
            <div className="text-lg font-semibold text-slate-900">{data.title}</div>
          </div>
          <button
            className="px-3 py-1.5 rounded-lg border text-slate-700 hover:bg-slate-50"
            onClick={onClose}
          >
            Cerrar
          </button>
        </div>

        {kpiKey === "topServicio" && (data.topPacientes?.length || 0) > 0 && (
          <div className="p-4 border-b">
            <div className="text-sm font-semibold text-slate-900 mb-3">
              Pacientes que contrataron este servicio
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {data.topPacientes.map((it) => (
                <div
                  key={it.paciente}
                  className="min-w-0 rounded-xl border bg-white px-3 py-3 flex items-center justify-between gap-3"
                >
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-slate-800 leading-snug break-words">
                      {it.paciente}
                    </div>
                    <div className="text-xs text-slate-500 mt-1">
                      {it.sesiones} sesión(es)
                    </div>
                  </div>

                  <div className="shrink-0 text-sm font-semibold text-slate-900 tabular-nums whitespace-nowrap">
                    {clp(it.total)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {kpiKey === "ocupacionSemana" && (data.serie?.length || 0) > 0 && (
          <div className="p-4 border-b">
            <div className="flex items-center justify-between mb-3">
              <div className="text-sm font-semibold text-slate-900">Sesiones por día</div>
              <div className="text-xs text-slate-500">Últimos 7 días</div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2">
              {data.serie.map((x) => {
                const dt = new Date(x.dia + "T00:00:00");
                const label = new Intl.DateTimeFormat("es-CL", { weekday: "short" }).format(dt);
                const day = new Intl.DateTimeFormat("es-CL", { day: "2-digit", month: "2-digit" }).format(dt);
                const pct = Math.round((Number(x.sesiones || 0) / maxSesiones) * 100);

                return (
                  <div key={x.dia} className="rounded-xl border bg-white p-2">
                    <div className="flex items-center justify-between">
                      <div className="text-[11px] text-slate-500 capitalize">{label}</div>
                      <div className="text-[11px] text-slate-400">{day}</div>
                    </div>

                    <div className="mt-1 text-2xl font-bold text-slate-900 tabular-nums">
                      {x.sesiones}
                    </div>

                    <div className="mt-2 h-1.5 w-full rounded-full bg-slate-100 overflow-hidden">
                      <div className="h-full bg-sky-500" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div className="p-4 border-b bg-slate-50">
          {(kpiKey === "ingresosMes" || kpiKey === "ingresosHistorico") && (
            <div className="flex flex-wrap items-center gap-3">
              <Badge>
                Total: <b className="ml-1">{clp(data.total)}</b>
              </Badge>
              <Badge>
                Sesiones realizadas: <b className="ml-1">{data.rows.length}</b>
              </Badge>
            </div>
          )}

          {kpiKey === "transferenciasMes" && (
            <div className="flex flex-wrap items-center gap-3">
              <Badge>
                Total: <b className="ml-1">{clp(data.total)}</b>
              </Badge>
              <Badge>
                Registros: <b className="ml-1">{data.rows.length}</b>
              </Badge>
            </div>
          )}

          {kpiKey === "estadoReservaDetalle" && (
            <div className="flex flex-wrap items-center gap-3">
              <Badge>
                Estado: <b className="ml-1">{data.estado}</b>
              </Badge>
              <Badge>
                Total: <b className="ml-1">{data.total}</b>
              </Badge>
              <Badge>
                Período: <b className="ml-1">{getRangeLabel(data.range)}</b>
              </Badge>
            </div>
          )}

          {kpiKey === "ticketPromedioMes" && (
            <div className="flex flex-wrap items-center gap-3">
              <Badge>
                Total: <b className="ml-1">{clp(data.total)}</b>
              </Badge>
              <Badge>
                Sesiones realizadas: <b className="ml-1">{data.rows.length}</b>
              </Badge>
              <Badge>
                Ticket promedio: <b className="ml-1">{clp(data.ticket)}</b>
              </Badge>
            </div>
          )}

          {kpiKey === "ocupacionSemana" && (
            <div className="flex flex-wrap items-center gap-3">
              <Badge>
                Reservas últimos 7 días: <b className="ml-1">{data.total}</b>
              </Badge>
              <Badge>
                Días con actividad: <b className="ml-1">{data.serie?.length || 0}</b>
              </Badge>
            </div>
          )}

          {kpiKey === "ocupacionDia" && (
            <div className="flex flex-wrap items-center gap-3">
              <Badge>
                Pacientes atendidos: <b className="ml-1">{data.total}</b>
              </Badge>
              <Badge>
                Reservas del día: <b className="ml-1">{data.reservasCount || 0}</b>
              </Badge>
            </div>
          )}

          {kpiKey === "conversion30d" && (
            <div className="flex flex-wrap items-center gap-3">
              <Badge>
                Total 30d: <b className="ml-1">{data.tot}</b>
              </Badge>
              <Badge>
                Aprobada/Realizada: <b className="ml-1">{data.conv}</b>
              </Badge>
              <Badge>
                Conversión: <b className="ml-1">{data.conversion}%</b>
              </Badge>
            </div>
          )}
        </div>

          {isIncomeDetail && (data.top?.length || 0) > 0 && (
            <>
              {/* Desktop / tablet */}
              <div className="hidden sm:block p-4 border-b">
                <div className="text-sm font-semibold text-slate-900 mb-2">
                  Top servicios (mes)
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {data.top.map((it) => (
                    <div
                      key={it.nombre}
                      className="flex items-center justify-between rounded-xl border p-2 gap-3"
                    >
                      <div className="text-sm text-slate-700 truncate pr-3 min-w-0">
                        {it.nombre}
                      </div>
                      <div className="text-sm font-semibold text-slate-900 whitespace-nowrap shrink-0">
                        {clp(it.total)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Mobile colapsable */}
              <div className="sm:hidden border-b">
                <button
                  type="button"
                  onClick={() => setShowTopServiciosMobile((v) => !v)}
                  className="w-full px-4 py-3 flex items-center justify-between text-left bg-white"
                >
                  <div>
                    <div className="text-sm font-semibold text-slate-900">
                      Top servicios (mes)
                    </div>
                    <div className="text-xs text-slate-500">
                      {showTopServiciosMobile ? "Ocultar detalle" : "Ver detalle"}
                    </div>
                  </div>

                  <div className="text-xs text-slate-500">
                    {showTopServiciosMobile ? "▲" : "▼"}
                  </div>
                </button>

                {showTopServiciosMobile && (
                  <div className="px-4 pb-4">
                    <div className="space-y-2">
                      {data.top.map((it) => (
                        <div
                          key={it.nombre}
                          className="rounded-xl border p-3 bg-white"
                        >
                          <div className="text-sm text-slate-700 leading-snug break-words">
                            {it.nombre}
                          </div>
                          <div className="mt-1 text-sm font-semibold text-slate-900">
                            {clp(it.total)}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </>
          )}

        {kpiKey === "conversion30d" && (data.mix?.length || 0) > 0 && (
          <div className="p-4 border-b">
            <div className="text-sm font-semibold text-slate-900 mb-2">Estados (30 días)</div>
            <div className="flex flex-wrap gap-2">
              {data.mix.map((it) => (
                <Badge key={it.estado}>
                  {it.estado}: <b className="ml-1">{it.cantidad}</b>
                </Badge>
              ))}
            </div>
          </div>
        )}

        {kpiKey === "topServicio" && (
          <div className="flex flex-wrap items-center gap-3">
            <Badge>
              Servicio: <b className="ml-1">{data.servicioNombre}</b>
            </Badge>
            <Badge>
              Total devengado: <b className="ml-1">{clp(data.total)}</b>
            </Badge>
            <Badge>
              Sesiones realizadas: <b className="ml-1">{data.sesiones || 0}</b>
            </Badge>
          </div>
        )}

        <div className="flex-1 min-h-0 p-4 overflow-y-auto overscroll-contain space-y-4">
          <div className="text-sm font-semibold text-slate-900 mb-2">Registros</div>

          <div className="overflow-auto rounded-xl border">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-100 text-slate-700">
                  <tr>
                    <th className="text-left px-3 py-2 whitespace-nowrap min-w-[110px]">Fecha</th>
                    {kpiKey === "ocupacionDia" && (
                      <th className="text-left px-3 py-2 whitespace-nowrap min-w-[72px]">Hora</th>
                    )}
                    <th className="text-left px-3 py-2 min-w-[140px]">Paciente</th>
                    <th className="text-left px-3 py-2 min-w-[220px]">Servicio</th>
                    <th className="text-left px-3 py-2 whitespace-nowrap min-w-[110px]">Estado</th>
                    <th className="text-left px-3 py-2 whitespace-nowrap min-w-[130px]">Estado sesión</th>
                    {kpiKey === "ocupacionDia" && (
                      <th className="text-right px-3 py-2 whitespace-nowrap min-w-[90px]">Pacientes</th>
                    )}
                    {(kpiKey === "ingresosMes" ||
                      kpiKey === "ticketPromedioMes" ||
                      kpiKey === "ingresosHistorico" ||
                      kpiKey === "topServicio" ||
                      kpiKey === "transferenciasMes") && (
                      <th className="text-right px-3 py-2 whitespace-nowrap min-w-[110px]">
                        {kpiKey === "transferenciasMes" ? "Monto" : "Ingreso"}
                      </th>
                    )}
                </tr>
              </thead>

              <tbody>
                {data.rows?.length ? (
                  data.rows.map((r) => {
                    const servicio = r.servicio_variante
                      ? `${r.servicio_categoria || "Servicio"} · ${r.servicio_variante}`
                      : r.servicio_categoria || r.servicio_raw || "—";

                    const paciente =
                      r.paciente_nombre || r.nombrePaciente || r.nombre || r.email || "—";

                    return (
                    <tr key={r.id} className="border-t align-top">
                      <td className="px-3 py-2 whitespace-nowrap">
                        {r.fecha || (r._when ? r._when.toISOString().slice(0, 10) : "—")}
                      </td>

                      {kpiKey === "ocupacionDia" && (
                        <td className="px-3 py-2 whitespace-nowrap">
                          {r.hora || "—"}
                        </td>
                      )}

                      <td className="px-3 py-2">
                        <div className="leading-snug break-words">{paciente}</div>
                      </td>

                      <td className="px-3 py-2">
                        <div className="leading-snug break-words text-slate-800">{servicio}</div>
                      </td>

                      <td className="px-3 py-2 whitespace-nowrap">
                        <Badge>{r.estado || "—"}</Badge>
                      </td>

                      <td className="px-3 py-2 whitespace-nowrap">
                        <Badge>{r.estadoSesion || "—"}</Badge>
                      </td>

                      {kpiKey === "ocupacionDia" && (
                        <td className="px-3 py-2 text-right tabular-nums">
                          {r._pacientes || 0}
                        </td>
                      )}

                      {(kpiKey === "ingresosMes" ||
                        kpiKey === "ticketPromedioMes" ||
                        kpiKey === "ingresosHistorico" ||
                        kpiKey === "topServicio" ||
                        kpiKey === "transferenciasMes") && (
                        <td className="px-3 py-2 text-right tabular-nums whitespace-nowrap">
                          {kpiKey === "transferenciasMes"
                            ? clp(r._monto || 0)
                            : clp(r._ingreso || 0)}
                        </td>
                      )}
                    </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td
                      className="px-3 py-6 text-slate-500"
                      colSpan={
                        kpiKey === "ocupacionDia"
                          ? 7
                          : (kpiKey === "ingresosMes" ||
                            kpiKey === "ticketPromedioMes" ||
                            kpiKey === "ingresosHistorico" ||
                            kpiKey === "topServicio" ||
                            kpiKey === "transferenciasMes")
                          ? 7
                          : 5
                      }
                    >
                      Sin registros para este KPI.
                    </td>
                  </tr>
                )}
              </tbody>


            </table>
          </div>

          <div className="mt-3 text-xs text-slate-500">
            * Detalle calculado desde las últimas {reservas?.length || 0} reservas cargadas (limit 1000).
          </div>
        </div>
      </div>
    </div>
  );
};

const KpiCard = ({ icon, title, value, accent, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="bg-white border border-gray-100 rounded-2xl shadow p-3 sm:p-4 overflow-hidden text-left hover:shadow-md transition cursor-pointer min-h-[96px]"
  >
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0 flex-1">
        <div className="text-[11px] sm:text-sm text-gray-500 leading-snug line-clamp-2">
          {title}
        </div>

        <div className="mt-1 font-bold text-blue-900 tabular-nums leading-tight text-lg sm:text-2xl">
          {value === null ? (
            <Skeleton className="h-7 w-24" />
          ) : (
            <span className="block truncate">{value}</span>
          )}
        </div>
      </div>

      <div
        className={`shrink-0 h-9 w-9 sm:h-10 sm:w-10 rounded-full flex items-center justify-center ${accent}`}
      >
        {icon}
      </div>
    </div>
  </button>
);

const cardClass =
  "bg-white border border-gray-100 rounded-2xl shadow p-3 sm:p-4 lg:p-5";

const chartHeightClass = "h-[220px] sm:h-[260px] lg:h-72";

const pieChartHeightClass = "h-[230px] sm:h-[280px] lg:h-80";

const Dashboard = () => {
  const [kpi, setKpi] = useState({
    ingresosMes: 0,
    transferenciasMes: 0,
    ocupacionSemana: 0,
    ticketPromedioMes: 0,
    conversion30d: 0,
  });
  const [loadingKpi, setLoadingKpi] = useState(true);

  const [reservas, setReservas] = useState([]);

  // Map de planes_usuarios por si alguna reserva no trae precio_por_sesion
  const [planUsuariosMap, setPlanUsuariosMap] = useState(new Map());

  const [ingresos6m, setIngresos6m] = useState([]);
  const [ocupacion7d, setOcupacion7d] = useState([]);
  const [topServicios, setTopServicios] = useState([]);
  const [mixEstados30d, setMixEstados30d] = useState([]);
  const [loadingCharts, setLoadingCharts] = useState(true);

  const yAxisWidthTop = useMemo(() => getYAxisWidthTop(topServicios), [topServicios]);

  const [detailOpen, setDetailOpen] = useState(false);
  const [detailKey, setDetailKey] = useState(null);
  const [detailMeta, setDetailMeta] = useState(null);
  const [ingresosRange, setIngresosRange] = useState(6);

  const [ocupacionRange, setOcupacionRange] = useState(7);
  const [ocupacionFecha, setOcupacionFecha] = useState("");

  const [mixEstadosRange, setMixEstadosRange] = useState(30);

  const [isMobile, setIsMobile] = useState(false);

  // Listener planes_usuarios (opcional pero útil como fallback)
  useEffect(() => {
    const qPlanes = query(
      collection(db, "planes_usuarios"),
      orderBy("creadoEn", "desc"),
      limit(2000)
    );

    const unsub = onSnapshot(qPlanes, (snap) => {
      const m = new Map();
      snap.docs.forEach((d) => {
        m.set(d.id, { id: d.id, ...d.data() });
      });
      setPlanUsuariosMap(m);
    });

    return () => unsub();
  }, []);

  useEffect(() => {
  const onResize = () => setIsMobile(window.innerWidth < 640);
  onResize();
  window.addEventListener("resize", onResize);
  return () => window.removeEventListener("resize", onResize);
  }, []);

  // Listener reservas + agregaciones
  useEffect(() => {
    const qBase = query(
      collection(db, "reservas"),
      orderBy("creadoEn", "desc"),
      limit(1000)
    );

    const unsub = onSnapshot(qBase, (snap) => {
      const docs = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setReservas(docs);

      const now = new Date();
      const startMonth = new Date(now.getFullYear(), now.getMonth(), 1);

      // ============================
      // KPIs (DEVENGADO POR SESIÓN)
      // ============================
      const realizadasMes = docs
        .filter((d) => esReservaRealizada(d))
        .filter((d) => {
          const f = getFechaContable(d);
          return f && f >= startMonth;
        });

      const ingresosMes = realizadasMes.reduce(
        (acc, d) => acc + getIngresoReconocido(d, planUsuariosMap),
        0
      );

      const sesionesRealizadasMes = realizadasMes.length || 0;
      const ticketPromedioMes = sesionesRealizadasMes
        ? Math.round(ingresosMes / sesionesRealizadasMes)
        : 0;

      // ============================
      // Ingresos históricos por mes
      // ============================
      const ingresos6mData = buildMonthlyIncomeSeries(docs, planUsuariosMap, ingresosRange);

      // ============================
      // Ocupación diaria (pacientes atendidos)
      // pendiente/aprobada/realizada
      // entrenamiento en pareja = x2
      // ============================
      const ocupacion7dData = buildDailyOccupancySeries(
        docs,
        ocupacionFecha ? null : ocupacionRange,
        ocupacionFecha || null
      );

      const capacidadBasePorDia = 10; // ajústalo a tu realidad
      const diasConsiderados = ocupacionFecha ? 1 : Number(ocupacionRange || 7);
      const capacidadPeriodo = capacidadBasePorDia * diasConsiderados;

      const totalPeriodo = ocupacion7dData.reduce((acc, x) => acc + (x.pacientes || 0), 0);
      const ocupacion = capacidadPeriodo
        ? Math.round((totalPeriodo / capacidadPeriodo) * 100)
        : 0;

      // ============================
      // Top servicios 90 días
      // (devengado por sesión)
      // ============================
      const desde90 = new Date(now);
      desde90.setDate(now.getDate() - 90);

      const accServicios = new Map();
      docs.forEach((d) => {
        if (!esReservaRealizada(d)) return;

        const when = getFechaContable(d);
        if (!when || when < desde90) return;

        const categoria = cleanLabel(d.servicio_categoria || "Servicio");
        const variante = cleanLabel(d.servicio_variante || "");
        const key = variante ? `${categoria} · ${variante}` : categoria;

        const val = getIngresoReconocido(d, planUsuariosMap);
        accServicios.set(key, (accServicios.get(key) || 0) + val);
      });

      const topServiciosArr = Array.from(accServicios.entries())
        .map(([nombre, total]) => ({ nombre, total }))
        .sort((a, b) => b.total - a.total)
        .slice(0, 5);

      const mix = buildMixEstadosSeries(docs, mixEstadosRange);

      const rowsForConversion = (docs || []).filter((d) => {
        const when = getDateFromReserva(d);
        const start = getRangeStartDate(30); // mantienes conversión en 30 días
        return when && start && when >= start;
      });

      const tot30 = rowsForConversion.length;
      const conv30 = rowsForConversion.filter((d) =>
        ["aprobada", "realizada"].includes(String(d.estado || "").toLowerCase())
      ).length;

      const conversion30d = tot30 ? Math.round((conv30 / tot30) * 100) : 0;

      const transferenciasMesRows = getTransferenciasMesRows(docs);
      const transferenciasMes = transferenciasMesRows.reduce(
        (acc, d) => acc + Number(d._monto || 0),
        0
      );

      // ============================
      // Set state
      // ============================
      setKpi({
        ingresosMes,
        transferenciasMes,
        ocupacionSemana: Math.max(0, Math.min(100, ocupacion)),
        ticketPromedioMes,
        conversion30d,
      });

      setIngresos6m(ingresos6mData);
      setOcupacion7d(ocupacion7dData);
      setTopServicios(topServiciosArr);
      setMixEstados30d(mix);

      setLoadingKpi(false);
      setLoadingCharts(false);
    });

    return () => unsub();
}, [planUsuariosMap, ingresosRange, ocupacionRange, ocupacionFecha, mixEstadosRange]);

  return (
    <div className="px-3 sm:px-4 lg:px-6 py-4 sm:py-6">
      <header className="mb-5 sm:mb-6">
        <h1 className="text-xl sm:text-2xl font-bold text-slate-900">
          Tablero de Control
        </h1>
        <p className="text-xs sm:text-sm text-slate-600">Resumen de operación.</p>
      </header>

      {/* KPIs */}
      <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3 sm:gap-4 mb-6">
        <KpiCard
          icon={<DollarSign className="h-5 w-5" />}
          title="Ingresos del mes (sesiones realizadas)"
          value={loadingKpi ? null : clp(kpi.ingresosMes)}
          accent="bg-ekine-yellow/20 text-ekine-blueDark"
          onClick={() => {
            setDetailKey("ingresosMes");
            setDetailMeta(null);
            setDetailOpen(true);
          }}
        />
        <KpiCard
          icon={<DollarSign className="h-5 w-5" />}
          title="Transferencias del mes"
          value={loadingKpi ? null : clp(kpi.transferenciasMes)}
          accent="bg-green-500/10 text-green-700"
          onClick={() => {
            setDetailKey("transferenciasMes");
            setDetailMeta(null);
            setDetailOpen(true);
          }}
        />
        <KpiCard
          icon={<TrendingUp className="h-5 w-5" />}
          title="Ocupación actual"
          value={loadingKpi ? null : `${kpi.ocupacionSemana}%`}
          accent="bg-ekine-blueLight/30 text-ekine-blueDark"
          onClick={() => {
            setDetailKey("ocupacionSemana");
            setDetailMeta(null);
            setDetailOpen(true);
          }}
        />
        <KpiCard
          icon={<DollarSign className="h-5 w-5" />}
          title="Ticket promedio del mes (realizadas)"
          value={loadingKpi ? null : clp(kpi.ticketPromedioMes)}
          accent="bg-ekine-grayLight text-ekine-blueDark"
          onClick={() => {
            setDetailKey("ticketPromedioMes");
            setDetailMeta(null);
            setDetailOpen(true);
          }}
        />
        <KpiCard
          icon={<TrendingUp className="h-5 w-5" />}
          title="Conversión (30 días)"
          value={loadingKpi ? null : `${kpi.conversion30d}%`}
          accent="bg-ekine-yellowLight text-ekine-blueDark"
          onClick={() => {
            setDetailKey("conversion30d");
            setDetailMeta(null);
            setDetailOpen(true);
          }}
        />
      </section>

      <section className="grid grid-cols-1 xl:grid-cols-3 gap-4 sm:gap-6 mb-6">
        <div className={`xl:col-span-2 ${cardClass}`}>
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-4">
            <div>
              <h3 className="font-semibold text-blue-900 mb-1">
                Ingresos por mes
              </h3>
              <p className="text-xs text-gray-500">
                Reconocidos por sesión trabajada (estado: realizada). Pasa el mouse para ver el monto y haz clic en un mes para ver el detalle.
              </p>
            </div>

            <div className="w-full sm:w-auto shrink-0">
              <label className="text-xs text-slate-500 block mb-1">Rango</label>
              <select
                value={ingresosRange}
                onChange={(e) =>
                  setIngresosRange(e.target.value === "all" ? "all" : Number(e.target.value))
                }
                className="w-full sm:w-auto rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 bg-white"
              >
                <option value={6}>Últimos 6 meses</option>
                <option value={12}>Últimos 12 meses</option>
                <option value={24}>Últimos 24 meses</option>
                <option value="all">Todo el historial</option>
              </select>
            </div>
          </div>

          <div className={chartHeightClass}>
            {loadingCharts ? (
              <Skeleton className="h-full w-full" />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart
                  data={ingresos6m}
                  margin={{
                    top: 8,
                    right: isMobile ? 6 : 20,
                    left: isMobile ? -22 : 0,
                    bottom: 0,
                  }}
                  onClick={(state) => {
                    const point = state?.activePayload?.[0]?.payload;
                    if (!point?.mes) return;

                    setDetailKey("ingresosHistorico");
                    setDetailMeta({ monthKey: point.mes });
                    setDetailOpen(true);
                  }}
                >
                  <defs>
                    <linearGradient id="gradIngreso" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#4f46e5" stopOpacity={0.25} />
                      <stop offset="95%" stopColor="#4f46e5" stopOpacity={0} />
                    </linearGradient>
                  </defs>

                  <CartesianGrid strokeDasharray="3 3" vertical={false} />

                  <XAxis
                    dataKey="mes"
                    tick={{ fontSize: isMobile ? 10 : 12 }}
                    tickMargin={8}
                    minTickGap={isMobile ? 24 : 12}
                    tickFormatter={(value) => formatMonthLabel(value)}
                  />

                  <YAxis
                    tick={{ fontSize: isMobile ? 10 : 12 }}
                    width={isMobile ? 36 : 52}
                    tickFormatter={(v) => {
                      if (v >= 1000000) return `${Math.round(v / 1000000)}M`;
                      if (v >= 1000) return `${Math.round(v / 1000)}k`;
                      return v;
                    }}
                  />

                  <Tooltip
                    formatter={(v) => clp(v)}
                    labelFormatter={(label) => formatMonthLabel(label)}
                  />

                  <Area
                    type="monotone"
                    dataKey="total"
                    stroke="#4f46e5"
                    strokeWidth={isMobile ? 2 : 2.5}
                    fill="url(#gradIngreso)"
                    activeDot={{
                      r: isMobile ? 4 : 6,
                      onClick: (_, payload) => {
                        const point = payload?.payload;
                        if (!point?.mes) return;

                        setDetailKey("ingresosHistorico");
                        setDetailMeta({ monthKey: point.mes });
                        setDetailOpen(true);
                      },
                      style: { cursor: "pointer" },
                    }}
                    style={{ cursor: "pointer" }}
                  />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        <div className={cardClass}>
          <div className="flex flex-col gap-3 mb-4">
            <div>
              <h3 className="font-semibold text-blue-900 mb-1">Ocupación diaria</h3>
              <p className="text-xs text-gray-500">
                Pacientes atendidos por día. Considera reservas pendientes, aprobadas y realizadas.
                Entrenamiento en pareja cuenta como 2 pacientes.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-2 sm:items-end">
              <div className="w-full sm:w-auto">
                <label className="text-xs text-slate-500 block mb-1">Rango</label>
                <select
                  value={ocupacionFecha ? "custom" : ocupacionRange}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v === "custom") {
                      setOcupacionFecha(new Date().toISOString().slice(0, 10));
                    } else {
                      setOcupacionFecha("");
                      setOcupacionRange(Number(v));
                    }
                  }}
                  className="w-full sm:w-auto rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 bg-white"
                >
                  <option value={7}>Últimos 7 días</option>
                  <option value={14}>Últimos 14 días</option>
                  <option value={21}>Últimos 21 días</option>
                  <option value={30}>Últimos 30 días</option>
                  <option value="custom">Fecha específica</option>
                </select>
              </div>

              {ocupacionFecha && (
                <div className="w-full sm:w-auto">
                  <label className="text-xs text-slate-500 block mb-1">Fecha</label>
                  <input
                    type="date"
                    value={ocupacionFecha}
                    onChange={(e) => setOcupacionFecha(e.target.value)}
                    className="w-full sm:w-auto rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 bg-white"
                  />
                </div>
              )}

              {ocupacionFecha && (
                <button
                  type="button"
                  onClick={() => {
                    setOcupacionFecha("");
                    setOcupacionRange(7);
                  }}
                  className="w-full sm:w-auto rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
                >
                  Volver a rango
                </button>
              )}
            </div>
          </div>

          <div className={chartHeightClass}>
            {loadingCharts ? (
              <Skeleton className="h-full w-full" />
            ) : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={ocupacion7d}
                margin={{
                  top: 8,
                  right: isMobile ? 4 : 10,
                  left: isMobile ? -22 : 0,
                  bottom: 0,
                }}
                barCategoryGap={isMobile ? "22%" : "30%"}
              >
                <CartesianGrid strokeDasharray="3 3" vertical={false} />

                <XAxis
                  dataKey="dia"
                  tick={{ fontSize: isMobile ? 10 : 11 }}
                  tickMargin={8}
                  minTickGap={isMobile ? 18 : 10}
                  tickFormatter={(value) => {
                    const dt = new Date(value + "T00:00:00");
                    return ocupacionFecha
                      ? new Intl.DateTimeFormat("es-CL", {
                          day: "2-digit",
                          month: "2-digit",
                        }).format(dt)
                      : new Intl.DateTimeFormat("es-CL", {
                          day: "2-digit",
                          month: "2-digit",
                        }).format(dt);
                  }}
                />

                <YAxis
                  allowDecimals={false}
                  tick={{ fontSize: isMobile ? 10 : 12 }}
                  width={isMobile ? 28 : 36}
                />

                <Tooltip
                  labelFormatter={(label) => {
                    const dt = new Date(label + "T00:00:00");
                    return new Intl.DateTimeFormat("es-CL", {
                      weekday: "long",
                      day: "2-digit",
                      month: "2-digit",
                      year: "numeric",
                    }).format(dt);
                  }}
                  formatter={(value) => [`${value} paciente(s)`, "Atendidos"]}
                />

                <Bar
                  dataKey="pacientes"
                  fill="#0ea5e9"
                  radius={[6, 6, 0, 0]}
                  maxBarSize={isMobile ? 24 : 36}
                  cursor="pointer"
                  onClick={(data) => {
                    if (!data?.dia) return;

                    setDetailKey("ocupacionDia");
                    setDetailMeta({ date: data.dia });
                    setDetailOpen(true);
                  }}
                />
              </BarChart>
            </ResponsiveContainer>
            )}
          </div>
        </div>
      </section>

      <section className="grid grid-cols-1 xl:grid-cols-2 gap-4 sm:gap-6">
        <div className={cardClass}>
          <h3 className="font-semibold text-blue-900 mb-1">Top servicios (90 días)</h3>
          <p className="text-xs text-gray-500 mb-4">
            Ingresos por sesión trabajada (estado: realizada). Haz clic en una barra para ver qué pacientes contrataron ese servicio.
          </p>

          <div className="min-h-[180px] sm:h-[260px] lg:h-72">
            {loadingCharts ? (
              <Skeleton className="h-full w-full" />
            ) : topServicios.length === 0 ? (
              <div className="p-6 text-sm text-gray-500">Sin datos suficientes.</div>
            ) : isMobile ? (
              <div className="space-y-2">
                {topServicios.slice(0, 3).map((item, idx) => {
                  const max = Math.max(...topServicios.map((x) => x.total || 0), 1);
                  const pct = Math.max(10, Math.round(((item.total || 0) / max) * 100));

                  return (
                    <button
                      key={item.nombre}
                      type="button"
                      onClick={() => {
                        setDetailKey("topServicio");
                        setDetailMeta({ servicioNombre: item.nombre });
                        setDetailOpen(true);
                      }}
                      className="w-full text-left rounded-xl border px-3 py-2.5 hover:bg-slate-50"
                    >
                      <div className="flex items-center justify-between gap-2 mb-1">
                        <div className="text-[11px] text-slate-400 shrink-0">#{idx + 1}</div>
                        <div className="text-xs font-semibold text-slate-900 whitespace-nowrap">
                          {clp(item.total)}
                        </div>
                      </div>

                      <div className="text-[13px] font-medium text-slate-800 leading-snug max-h-[2.6rem] overflow-hidden">
                        {item.nombre}
                      </div>

                      <div className="mt-2 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                        <div
                          className="h-full rounded-full bg-emerald-500"
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    </button>
                  );
                })}

                {topServicios.length > 3 && (
                  <div className="pt-1 text-[11px] text-slate-500 text-center">
                    Mostrando 3 de {topServicios.length} servicios.
                  </div>
                )}
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={topServicios}
                  layout="vertical"
                  margin={{ top: 12, right: 20, left: 10, bottom: 12 }}
                >
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 12 }} />
                  <YAxis
                    type="category"
                    dataKey="nombre"
                    width={yAxisWidthTop}
                    interval={0}
                    tickMargin={10}
                    tick={<YAxisTickMultiline />}
                  />
                  <Tooltip
                    formatter={(v) => clp(v)}
                    labelFormatter={(label) => `Servicio: ${label}`}
                  />
                  <Bar
                    dataKey="total"
                    fill="#10b981"
                    radius={[0, 6, 6, 0]}
                    cursor="pointer"
                    onClick={(data) => {
                      if (!data?.nombre) return;

                      setDetailKey("topServicio");
                      setDetailMeta({ servicioNombre: data.nombre });
                      setDetailOpen(true);
                    }}
                  />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        <div className={cardClass}>
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-4">
            <div>
              <h3 className="font-semibold text-blue-900 mb-1">
                Mix de estados ({getRangeLabel(mixEstadosRange)})
              </h3>
              <p className="text-xs text-gray-500">
                Distribución de estados de reservas. Haz clic en cualquier color del gráfico para ver el detalle de ese estado.
              </p>
            </div>

            <div className="w-full sm:w-auto shrink-0">
              <label className="text-xs text-slate-500 block mb-1">Rango</label>
              <select
                value={mixEstadosRange}
                onChange={(e) =>
                  setMixEstadosRange(
                    e.target.value === "all" ? "all" : Number(e.target.value)
                  )
                }
                className="w-full sm:w-auto rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 bg-white"
              >
                <option value={7}>Últimos 7 días</option>
                <option value={30}>Últimos 30 días</option>
                <option value={90}>Últimos 3 meses</option>
                <option value={180}>Últimos 6 meses</option>
                <option value={365}>Últimos 12 meses</option>
                <option value="all">Todo el historial</option>
              </select>
            </div>
          </div>

          <div className={pieChartHeightClass}>
            {loadingCharts ? (
              <Skeleton className="h-full w-full" />
            ) : mixEstados30d.length === 0 ? (
              <div className="p-6 text-sm text-gray-500">Sin datos suficientes.</div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={mixEstados30d}
                    dataKey="cantidad"
                    nameKey="estado"
                    innerRadius={isMobile ? 42 : 60}
                    outerRadius={isMobile ? 68 : 90}
                    paddingAngle={2}
                    cx="50%"
                    cy={isMobile ? "42%" : "45%"}
                    cursor="pointer"
                    onClick={(data) => {
                      if (!data?.estado) return;

                      setDetailKey("estadoReservaDetalle");
                      setDetailMeta({
                        estado: data.estado,
                        range: mixEstadosRange,
                      });
                      setDetailOpen(true);
                    }}
                  >
                    {mixEstados30d.map((entry, i) => (
                      <Cell
                        key={`${entry.estado}-${i}`}
                        fill={getEstadoColor(entry.estado)}
                        style={{ cursor: "pointer" }}
                        onClick={() => {
                          setDetailKey("estadoReservaDetalle");
                          setDetailMeta({
                            estado: entry.estado,
                            range: mixEstadosRange,
                          });
                          setDetailOpen(true);
                        }}
                      />
                    ))}
                  </Pie>

                  {!isMobile && <Legend />}

                  <Tooltip
                    formatter={(value, name) => [`${value} reserva(s)`, cleanLabel(name)]}
                  />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>

          {!loadingCharts && mixEstados30d.length > 0 && (
            <div className="mt-4 flex flex-wrap gap-2 justify-start">
              {mixEstados30d.map((it, i) => (
                <button
                  key={it.estado}
                  type="button"
                  onClick={() => {
                    setDetailKey("estadoReservaDetalle");
                    setDetailMeta({
                      estado: it.estado,
                      range: mixEstadosRange,
                    });
                    setDetailOpen(true);
                  }}
                  className="inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-[11px] sm:text-xs text-slate-700 hover:bg-slate-50"
                >
                  <span
                    className="inline-block h-2.5 w-2.5 rounded-full"
                    style={{ backgroundColor: getEstadoColor(it.estado) }}
                  />
                  <span>{it.estado}</span>
                  <span className="font-semibold">{it.cantidad}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </section>

        <KpiDetailModal
          open={detailOpen}
          onClose={() => setDetailOpen(false)}
          kpiKey={detailKey}
          reservas={reservas}
          planUsuariosMap={planUsuariosMap}
          detailMeta={detailMeta}
        />
    </div>
  );
};

export default Dashboard;
